import type { BasketItem } from '../page';

interface OrderDiscount {
  type: 'percentage' | 'fixed';
  value: number;
}

interface TotalsPanelProps {
  basketItems: BasketItem[];
  orderDiscount: OrderDiscount | null;
  onRemoveDiscount: () => void;
}

export default function TotalsPanel({ basketItems, orderDiscount, onRemoveDiscount }: TotalsPanelProps) {
  // Calculate subtotal from all items
  const subtotal = basketItems.reduce((sum, item) => sum + item.total, 0);
  
  // Calculate item discounts (items with discount flag)
  const itemDiscounts = basketItems
    .filter(item => item.discount)
    .reduce((sum, item) => sum + (item.price * item.qty * 0.1), 0); // Assuming 10% discount
  
  // Calculate order discount amount
  let orderDiscountAmount = 0;
  if (orderDiscount) {
    if (orderDiscount.type === 'percentage') {
      orderDiscountAmount = (subtotal - itemDiscounts) * (orderDiscount.value / 100);
    } else {
      orderDiscountAmount = orderDiscount.value;
    }
  }
  
  // Calculate tax (9% on taxable items after discounts)
  const subtotalAfterDiscounts = subtotal - itemDiscounts - orderDiscountAmount;
  const tax = basketItems
    .filter(item => item.tax)
    .reduce((sum, item) => {
      const itemSubtotal = item.total;
      const itemAfterDiscount = itemSubtotal * (subtotalAfterDiscounts / subtotal);
      return sum + (itemAfterDiscount * 0.09);
    }, 0);
  
  const bagFee = basketItems.length > 0 ? 0.25 : 0;
  
  // Calculate rounding (round to nearest 5 cents)
  const totalBeforeRounding = subtotalAfterDiscounts + tax + bagFee;
  const roundedTotal = Math.round(totalBeforeRounding * 20) / 20; // Round to nearest 0.05
  const rounding = roundedTotal - totalBeforeRounding;
  
  // Calculate grand total
  const grandTotal = roundedTotal;

  return (
    <div className="bg-white border-b border-l border-gray-200 p-4">
      <h2 className="text-sm font-bold text-gray-900 mb-4 whitespace-nowrap">Transaction Summary</h2>
      
      <div className="space-y-2.5 mb-4">
        <div className="flex items-center justify-between">
          <span className="text-sm text-gray-600 whitespace-nowrap">Subtotal</span>
          <span className="text-sm font-semibold text-gray-900 whitespace-nowrap">${subtotal.toFixed(2)}</span>
        </div>
        
        {itemDiscounts > 0 && (
          <div className="flex items-center justify-between">
            <span className="text-sm text-orange-600 whitespace-nowrap">Item Discounts</span>
            <span className="text-sm font-semibold text-orange-600 whitespace-nowrap">-${itemDiscounts.toFixed(2)}</span>
          </div>
        )}
        
        {orderDiscount && orderDiscountAmount > 0 && (
          <div className="flex items-center justify-between group">
            <div className="flex items-center gap-2">
              <span className="text-sm text-orange-600 whitespace-nowrap">
                Order Discount ({orderDiscount.type === 'percentage' ? `${orderDiscount.value}%` : `$${orderDiscount.value.toFixed(2)}`})
              </span>
              <button
                onClick={onRemoveDiscount}
                className="opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer"
                title="Remove discount"
              >
                <i className="ri-close-circle-fill text-red-500 text-base"></i>
              </button>
            </div>
            <span className="text-sm font-semibold text-orange-600 whitespace-nowrap">-${orderDiscountAmount.toFixed(2)}</span>
          </div>
        )}
        
        <div className="flex items-center justify-between">
          <span className="text-sm text-gray-600 whitespace-nowrap">Tax (VAT 9%)</span>
          <span className="text-sm font-semibold text-gray-900 whitespace-nowrap">${tax.toFixed(2)}</span>
        </div>
        
        {bagFee > 0 && (
          <div className="flex items-center justify-between">
            <span className="text-sm text-gray-600 whitespace-nowrap">Bag Fee</span>
            <span className="text-sm font-semibold text-gray-900 whitespace-nowrap">${bagFee.toFixed(2)}</span>
          </div>
        )}
        
        {rounding !== 0 && (
          <div className="flex items-center justify-between">
            <span className="text-sm text-gray-600 whitespace-nowrap">Rounding</span>
            <span className="text-sm font-semibold text-gray-900 whitespace-nowrap">${rounding.toFixed(2)}</span>
          </div>
        )}
      </div>

      <div className="pt-4 border-t-2 border-gray-300 mb-4">
        <div className="flex items-center justify-between">
          <span className="text-lg font-bold text-gray-900 whitespace-nowrap">Grand Total</span>
          <span className="text-3xl font-bold text-teal-600 whitespace-nowrap">${grandTotal.toFixed(2)}</span>
        </div>
      </div>

      <div className="space-y-2 p-3 bg-gray-50 rounded-lg">
        <h3 className="text-xs font-bold text-gray-700 mb-2 whitespace-nowrap">Checkout Status</h3>
        
        <div className="flex items-center justify-between">
          <span className="text-xs text-gray-600 whitespace-nowrap">Blocking Issues</span>
          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-green-100 text-green-700 whitespace-nowrap">
            0 Issues
          </span>
        </div>
        
        <div className="flex items-center justify-between">
          <span className="text-xs text-gray-600 whitespace-nowrap">Customer Attached</span>
          <div className="flex items-center gap-1.5">
            <div className="w-1.5 h-1.5 rounded-full bg-green-500"></div>
            <span className="text-xs font-semibold text-green-700 whitespace-nowrap">Yes</span>
          </div>
        </div>
        
        <div className="flex items-center justify-between">
          <span className="text-xs text-gray-600 whitespace-nowrap">Ready to Tender</span>
          <div className="flex items-center gap-1.5">
            <div className="w-1.5 h-1.5 rounded-full bg-green-500"></div>
            <span className="text-xs font-semibold text-green-700 whitespace-nowrap">Ready</span>
          </div>
        </div>
      </div>
    </div>
  );
}
